import type { Filter, QueryData, User } from './types'
import type { FilterDropdownProps, SorterResult } from 'antd/es/table/interface'

import { SearchOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Button, Card, DatePicker, Space, Table } from 'antd'
import cx from 'classnames'
import React, { useState } from 'react'
import dayjs from 'dayjs'

import $api from '../../../API'

import { ExpandedRow } from './expanded-row'
import { currencyFormatter } from './functions'
import { BalanceType } from './types'

export function PinnacleTpaPage(): React.JSX.Element {
  const [filter, setFilter] = useState<Filter>({
    sort: 'date',
    order: 'DESC',
    page: 0,
    perPage: 10
  })
  const [dateFilter, setDateFilter] = useState<Date | null>(null)
  const query = useQuery<unknown, unknown, QueryData, ['pinnacle-tpa-data', Filter]>({
    queryKey: ['pinnacle-tpa-data', filter],

    async queryFn() {
      const { data } = await $api.get<QueryData>('pinnacle/tpa', {
        params: filter
      })

      return {
        users: data.users
      }
    }
  })

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: keyof Filter
  ): void => {
    confirm()
    setFilter(f => ({
      ...f,
      [dataIndex]: dataIndex === 'date' ? dateFilter : selectedKeys[0]
    }))
  }

  const handleDate = (date?: Date): void => {
    if (date) {
      setDateFilter(date)
    }
  }

  const handleReset = (
    clearFilters: () => void,
    dataIndex: keyof Filter,
    confirm: FilterDropdownProps['confirm']
  ): void => {
    clearFilters()
    setFilter(f => {
      const { [dataIndex]: _, ...rest } = f
      return rest as Filter
    })
    confirm()
  }

  return (
    <div className='flex flex-col gap-4 p-6 font-normal rounded-lg font-lato bg-lucent-background text-lucent-text'>
      <div className='flex justify-between items-start'>
        <div className='flex gap-6 items-center'>
          <h1 className='text-2xl'>FID Account Details</h1>
        </div>
      </div>
      <Card
        className='w-full rounded-xl'
        bodyStyle={{
          padding: '1rem'
        }}
      >
        <Table
          size='small'
          rowKey='account_number'
          dataSource={query.data?.users}
          loading={query.isLoading}
          sortDirections={['ascend', 'descend']}
          pagination={false}
          onChange={(
            _pagination,
            _filters,
            sorter: SorterResult<User> | Array<SorterResult<User>>,
            _extra
          ) => {
            const selectedSorted = Array.isArray(sorter) ? sorter[0] : sorter
            if (selectedSorted?.order) {
              setFilter(f => ({
                ...f,
                sort: selectedSorted.field as keyof User,
                order: selectedSorted.order === 'descend' ? 'DESC' : 'ASC'
              }))
            }
          }}
          expandable={{
            rowExpandable: (record: QueryData['users'][0]) => record.transactions.length > 0,
            expandedRowRender: (record: QueryData['users'][0]) => (
              <ExpandedRow row={record.transactions} />
            )
          }}
          columns={[
            {
              key: 'title',
              dataIndex: 'title',
              title: 'TPA',
              width: 100
            },
            {
              key: 'date',
              dataIndex: 'date',
              title: 'Date',
              render: (date: string) => dayjs(date).tz('America/Chicago').format('MM/DD/YYYY'),
              filterDropdown: ({ selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                  <DatePicker
                    onChange={e => {
                      handleDate(e?.toDate())
                    }}
                    allowClear
                  />
                  <Space>
                    <Button
                      type='primary'
                      onClick={() => {
                        handleSearch(selectedKeys as string[], confirm, 'date')
                      }}
                      icon={<SearchOutlined />}
                      size='small'
                      style={{ width: 90 }}
                    >
                      Search
                    </Button>
                    <Button
                      onClick={() => {
                        if (clearFilters) {
                          handleReset(clearFilters, 'date', confirm)
                        }
                      }}
                      size='small'
                      style={{ width: 90 }}
                    >
                      Reset
                    </Button>
                  </Space>
                </div>
              ),
              filterIcon: () => (
                <SearchOutlined
                  className={cx({
                    'text-[#1890ff]': filter.date !== undefined,
                    'animate-pulse': query.isLoading && filter.date !== undefined
                  })}
                />
              )
            },
            {
              key: 'account_number',
              dataIndex: 'account_number',
              title: 'Account Number'
            },
            ...[
              'total_credits',
              'total_debits',
              'opening_ledger',
              'closing_ledger',
              'opening_available'
            ].map(key => ({
              key,
              dataIndex: key,
              title: BalanceType[key as keyof typeof BalanceType],
              sorter: true,
              showSorterTooltip: false,
              className: 'show-default-sort text-xs [&_.ant-table-column-title]:text-center',
              render: (value: number | null) => {
                if (value !== null) {
                  return currencyFormatter(value / 100)
                }
                return '-'
              }
            })),
            {
              key: 'file_name',
              dataIndex: 'file_name',
              title: 'File name'
            },
            {
              key: 'date',
              dataIndex: 'date',
              title: 'File date',
              render: (date: string) => dayjs(date).tz('America/Chicago').format('MM/DD/YYYY HH:mm')
            },
            {
              key: 'sender',
              dataIndex: 'sender',
              title: 'Sender'
            }
          ]}
        />
      </Card>
    </div>
  )
}
